import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import PricingPage from "./pages/PricingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import CoffretTemoin from "./pages/CoffretTemoin";
import AdminPage from "./pages/AdminPage";
import SasAlertsPreviewPage from "./pages/SasAlertsPreviewPage";
import ServicesPage from "./pages/ServicesPage";
import LivrePhotoPage from "./pages/LivrePhotoPage";
import NumerisationPage from "./pages/NumerisationPage";
import SharedGalleryPage from "./pages/SharedGalleryPage";
import EclaireurView from "./pages/EclaireurView";
import AccountPage from "./pages/AccountPage";
import TermsPage from "./pages/TermsPage";
import FaqPage from "./pages/FaqPage";
import PrivacyPage from "./pages/PrivacyPage";
import SplashScreen from "./components/SplashScreen";
import OnboardingSlides from "./components/OnboardingSlides";
import ComparisonSlider from "./components/ComparisonSlider";
import PaywallModal from "./components/PaywallModal";
import BobineLogo from "./components/BobineLogo";
import BobineLoader from "./components/BobineLoader";
import VerifyEmail from "./components/VerifyEmail";
import LanguageSelector from "./components/LanguageSelector";
import MobileNav from "./components/MobileNav";
import BobineVideoPlayer from "./components/BobineVideoPlayer";
import TresorsCounter from "./components/TresorsCounter";
import TestimonialsSection from "./components/TestimonialsSection";
import FondatriceViewCounter from "./components/FondatriceViewCounter";
import DemoPage from "./pages/DemoPage";
import DemoExitBanner from "./components/DemoExitBanner";
import { toast, Toaster } from "sonner";

import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live');
const API = `${BACKEND_URL}/api`;

/* Admin route protection — only role=admin can access */
const ProtectedAdminRoute = ({ user, children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('bobine_token');
    if (!token || !user) { navigate('/'); return; }
    fetch(`${API}/auth/check-role`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.role === 'admin') setAllowed(true); else navigate('/dashboard'); })
      .catch(() => navigate('/dashboard'))
      .finally(() => setChecking(false));
  }, [user, navigate]);

  if (checking) return null;
  return allowed ? children : null;
};


const Home = () => {
  const { t } = useLanguage();
  const [images, setImages] = useState({ original: null, watermarked: null });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [pollStatus, setPollStatus] = useState(null);
  const token = localStorage.getItem('bobine_token');
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bobine_user')); } catch { return null; }
  });
  const [saved, setSaved] = useState(false);
  const [storageFull, setStorageFull] = useState(null);
  const [showNavUserMenu, setShowNavUserMenu] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccessReveal, setShowSuccessReveal] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ═══ VERROUILLAGE ABSOLU ═══
  // Règle de Bobine : aucun visiteur non connecté ne doit voir le contenu de l'application.
  // La seule page accessible sans compte est /compte (inscription/login). Tout le reste est redirigé.
  // Exceptions : /terms, /privacy, /faq, /univers, /offres, /services, etc. (voir Routes)
  useEffect(() => {
    if (!token && !searchParams.get('session_id')) {
      navigate('/compte', { replace: true });
    }
  }, [token, navigate, searchParams]);

  // Polling paiement au retour de Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPollStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setPollStatus('success');
        setSearchParams({});
        return;
      }
      if (res.data.status === 'expired') {
        setPollStatus('expired');
        return;
      }
      setPollStatus('pending');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPollStatus('error');
    }
  }, [setSearchParams]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (payment === 'success' && sessionId) {
      setPollStatus('pending');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const handleFileUpload = async (files) => {
    const fileList = Array.from(files).filter(f => {
      if (f.type.startsWith('image/')) return true;
      if (f.type === 'application/octet-stream') return true;
      const name = (f.name || '').toLowerCase();
      return name.endsWith('.heic') || name.endsWith('.heif') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp');
    });
    if (!fileList.length) { setError("Veuillez sélectionner des images"); return; }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setProcessedCount(0);
    setTotalFiles(fileList.length);

    // Conversion HEIC → JPG en pipeline (in-worker pour ne pas bloquer la progression)
    const { convertHeicIfNeeded, isHeicFile } = await import('./lib/watermark');
    const heicCount = fileList.filter(isHeicFile).length;
    if (heicCount > 0) {
      toast(`${heicCount} photo${heicCount > 1 ? 's' : ''} HEIC d\u00e9tect\u00e9e${heicCount > 1 ? 's' : ''} \u2014 conversion automatique en cours...`, {
        id: 'heic-convert', duration: 5000,
        style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#F7E6BD' },
      });
    }

    const results = [];
    const failures = [];
    const fileProgress = new Array(fileList.length).fill(0); // Track each file's progress (0-1)
    let done = 0;

    // Smooth progress updater — aggregates all file progress
    const progressInterval = setInterval(() => {
      const totalPct = fileProgress.reduce((sum, p) => sum + p, 0) / fileList.length * 100;
      setUploadProgress(Math.round(Math.min(totalPct, 99)));
    }, 100);

    // Upload a single file and track its progress (with HEIC conversion inline)
    const uploadOne = async (rawFile, idx) => {
      try {
        // Conversion HEIC inline (in-worker)
        const file = await convertHeicIfNeeded(rawFile);
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/watermark/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            // Upload phase = 0 to 0.5, processing phase = 0.5 to 1.0
            fileProgress[idx] = (e.loaded / e.total) * 0.5;
          },
        });
        // Upload done, server responded → file fully processed
        fileProgress[idx] = 1;
        if (response.data.status === 'success') {
          results.push({ original: response.data.original, watermarked: response.data.watermarked, fileIdx: idx, filename: file.name });
        }
      } catch (e) {
        fileProgress[idx] = 1; // Mark as done even on error
        // Track failure reason for diagnostic
        const reason = e?.response?.status === 413 ? 'too_large'
          : e?.response?.status >= 500 ? 'server_error'
          : e?.response?.status === 415 || e?.response?.status === 400 ? 'unsupported'
          : 'network';
        failures.push({ name: rawFile.name, reason });
      }
      done++;
      setProcessedCount(done);
    };

    // Parallel upload with concurrency pool (mobile=1 séquentiel pour HEIC mémoire, desktop=3)
    const isMobile = window.innerWidth < 768;
    const CONCURRENCY = isMobile ? 1 : 3;
    const queue = [...fileList.map((f, i) => ({ file: f, idx: i }))];
    const workers = [];

    const runWorker = async () => {
      while (queue.length > 0) {
        const { file, idx } = queue.shift();
        await uploadOne(file, idx);
      }
    };

    for (let w = 0; w < Math.min(CONCURRENCY, fileList.length); w++) {
      workers.push(runWorker());
    }
    await Promise.all(workers);

    clearInterval(progressInterval);

    if (results.length > 0) {
      setUploadProgress(100);

      // Auto-sauvegarde groupée si l'utilisateur est connecté
      let savedCount = results.length;
      if (token) {
        try {
          // Sort by original file index to keep order
          results.sort((a, b) => a.fileIdx - b.fileIdx);
          const photosToSave = results.map((r) => ({
            watermarked: r.watermarked,
            filename: r.filename || fileList[r.fileIdx]?.name || `souvenir-${r.fileIdx + 1}.jpg`,
          }));
          const saveRes = await fetch(`${API}/photos/save-batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ photos: photosToSave }),
          });
          if (saveRes.status === 403) {
            // Quota atteint → rediriger vers les offres
            setUploading(false);
            setUploadProgress(0);
            await new Promise(r => setTimeout(r, 300));
            setImages(results[results.length - 1]);
            navigate('/offres');
            return;
          }
          // Lecture du compteur réel sauvegardé pour feedback fiable
          try {
            const saveData = await saveRes.json();
            if (typeof saveData.saved_count === 'number') savedCount = saveData.saved_count;
          } catch {}
        } catch (e) { /* sauvegarde silencieuse */ }
      }

      // Feedback explicite si certaines photos n'ont pas suivi
      const totalRequested = fileList.length;
      if (savedCount < totalRequested) {
        const missing = totalRequested - savedCount;
        const reason413 = failures.some(f => f.reason === 'too_large');
        const reasonUnsupported = failures.some(f => f.reason === 'unsupported');
        let hint = '';
        if (reason413) hint = ' Format trop lourd ?';
        else if (reasonUnsupported) hint = ' Format non reconnu (HEIC iPhone ?).';
        toast(`${savedCount} sur ${totalRequested} photo${totalRequested > 1 ? 's' : ''} ajoutée${savedCount > 1 ? 's' : ''} au SAS.${hint}`, {
          description: missing === 1
            ? `1 photo n'a pas pu être traitée. R\u00e9essayez ou changez le format.`
            : `${missing} photos n'ont pas pu être traitées. R\u00e9essayez ou changez le format.`,
          duration: 9000,
          style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#F7E6BD' },
        });
      } else if (savedCount > 0 && totalRequested > 1) {
        toast(`${savedCount} photos ajoutées au SAS \u2728`, {
          duration: 4000,
          style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#F7E6BD' },
        });
      }

      await new Promise(r => setTimeout(r, 300));
      setImages(results[results.length - 1]);
      setShowSuccessReveal(true);
    } else {
      setError("Erreur lors du traitement de vos photos");
    }

    setUploading(false);
    setUploadProgress(0);
  };

  const handleFileSelect = (e) => handleFileUpload(e.target.files);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFileUpload(e.dataTransfer.files);
  };

  const resetUpload = () => {
    setImages({ original: null, watermarked: null });
    setError(null);
    setShowSuccessReveal(false);
    setProcessedCount(0);
    setTotalFiles(0);
    setPaymentSuccess(false);
    setPollStatus(null);
    setSaved(false);
  };

  const handleDownloadOrSave = (e) => {
    if (paymentSuccess) return; // laisser le téléchargement se faire
    e.preventDefault();
    setShowPaywall(true);
  };

  const handleSaveToTresors = async () => {
    const token = localStorage.getItem('bobine_token');
    if (!token) {
      navigate('/compte');
      return;
    }
    try {
      await axios.post(`${API}/photos/save`, {
        watermarked: images.watermarked,
        filename: 'souvenir.jpg',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setStorageFull(null);
    } catch (e) {
      if (e.response?.status === 403) {
        setStorageFull(e.response.data.detail);
      }
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000000' }}>
      <Toaster position="top-center" richColors={false} closeButton />
      <style>{`
        @keyframes bobineGlow {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(212,175,55,0.3)); }
          50% { filter: drop-shadow(0 0 20px rgba(212,175,55,0.7)) drop-shadow(0 0 40px rgba(212,175,55,0.2)); }
        }
        @keyframes bobineSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <BobineLogo size={22} />

            {/* Navigation — unified for desktop & mobile */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Desktop-only: language */}
              <div className="hidden md:flex items-center gap-2">
                <LanguageSelector />
              </div>

              {token && user ? (
                /* Logged in: Bobine + User name dropdown (all screens) */
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowNavUserMenu(!showNavUserMenu)}
                    data-testid="nav-user-menu-toggle"
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37',
                      background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.3)',
                      borderRadius: 24, padding: '6px 14px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                      transition: 'all 0.3s',
                      boxShadow: showNavUserMenu ? '0 0 16px rgba(212,175,55,0.15)' : 'none',
                    }}
                  >
                    <img src="/bobine-reel-transparent.png" alt="" style={{ width: 22, height: 22, objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(212,175,55,0.3))' }} />
                    <span style={{ fontWeight: 600, letterSpacing: 0.3, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name?.split(' ')[0]}</span>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" style={{ transition: 'transform 0.3s', transform: showNavUserMenu ? 'rotate(180deg)' : 'none' }}><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  {showNavUserMenu && (
                    <>
                      <div onClick={() => setShowNavUserMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                      <div data-testid="nav-user-menu-dropdown" style={{
                        position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
                        backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.25)',
                        borderRadius: 14, minWidth: 260, overflow: 'hidden',
                        boxShadow: '0 16px 48px rgba(0,0,0,0.8), 0 0 0 1px rgba(212,175,55,0.08)',
                        backdropFilter: 'blur(12px)',
                      }}>
                        {/* User info header */}
                        <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid rgba(212,175,55,0.1)', background: 'linear-gradient(135deg, rgba(212,175,55,0.06), transparent)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(212,175,55,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(212,175,55,0.05)' }}>
                              <img src="/bobine-reel-transparent.png" alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                            </div>
                            <div>
                              <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 600, margin: 0 }}>{user.name}</p>
                              <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#FAF3E0', margin: '2px 0 0' }}>{user.email}</p>
                            </div>
                          </div>
                        </div>

                        {/* Navigation links (visible for all, essential on mobile) */}
                        <div style={{ padding: '6px 0', borderBottom: '1px solid rgba(212,175,55,0.08)' }}>
                          {[
                            { testid: 'nav-menu-vault', label: t('dash_vault_title') || 'Mon Coffre-fort', icon: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z', action: () => navigate('/dashboard') },
                            { testid: 'nav-menu-tdb', label: 'Tableau de bord', icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1h-4a1 1 0 01-1-1v-5z', action: () => navigate('/dashboard') },
                            { testid: 'nav-menu-account', label: t('menu_account') || 'Mon Profil', icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', action: () => navigate('/compte') },
                            { testid: 'nav-menu-plans', label: t('menu_plans') || 'Offres & Tarifs', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', action: () => navigate('/offres') },
                          ].map((item) => (
                            <button key={item.testid} onClick={() => { setShowNavUserMenu(false); item.action(); }} data-testid={item.testid}
                              style={{
                                width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12,
                                fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37',
                                fontWeight: 700,
                                background: 'none', border: 'none', padding: '11px 18px',
                                cursor: 'pointer', transition: 'all 0.2s',
                              }}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={item.icon} /></svg>
                              {item.label}
                            </button>
                          ))}
                        </div>

                        {/* Services & Explore (essential for mobile users) */}
                        <div style={{ padding: '6px 0', borderBottom: '1px solid rgba(212,175,55,0.08)' }}>
                          {[
                            { testid: 'nav-menu-services', label: t('dash_prestige') || 'Services de Prestige', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', action: () => navigate('/services') },
                            { testid: 'nav-menu-home', label: t('nav_home') || 'Accueil', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1', action: () => navigate('/') },
                          ].map((item) => (
                            <button key={item.testid} onClick={() => { setShowNavUserMenu(false); item.action(); }} data-testid={item.testid}
                              style={{
                                width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12,
                                fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37',
                                fontWeight: 700,
                                background: 'none', border: 'none', padding: '10px 18px',
                                cursor: 'pointer', transition: 'all 0.2s',
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={item.icon} /></svg>
                              {item.label}
                            </button>
                          ))}
                          {/* Language selector for mobile */}
                          <div className="md:hidden" style={{ padding: '6px 18px 8px' }}>
                            <LanguageSelector />
                          </div>
                        </div>

                        {/* Logout */}
                        <button onClick={() => { setShowNavUserMenu(false); localStorage.removeItem('bobine_token'); localStorage.removeItem('bobine_user'); setUser(null); navigate('/'); }}
                          data-testid="nav-logout-button"
                          style={{
                            width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12,
                            fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0',
                            fontWeight: 700,
                            background: 'none', border: 'none', padding: '11px 18px', cursor: 'pointer', transition: 'all 0.2s',
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                          {t('dash_logout') || 'Deconnexion'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Not logged in: compact buttons for all screens */
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={() => navigate('/compte')}
                    data-testid="nav-auth-button"
                    style={{
                      fontFamily: "'Georgia', serif",
                      backgroundColor: 'transparent', color: '#D4AF37',
                      border: 'none', padding: '6px 12px', fontSize: 12, letterSpacing: 0.5,
                      cursor: 'pointer', transition: 'all 0.3s',
                    }}
                  >
                    {t('auth_connect')}
                  </button>
                  <button
                    onClick={() => navigate(token ? '/dashboard' : '/compte')}
                    data-testid="nav-account-button"
                    style={{
                      fontFamily: "'Georgia', serif",
                      background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
                      border: 'none',
                      padding: '6px 14px', borderRadius: 20, fontSize: 12, letterSpacing: 0.5,
                      fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s',
                      boxShadow: 'none',
                    }}
                  >
                    {token ? t('dash_vault_title') : 'Créer mon coffre'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Fronton de bienvenue — accueil public */}
          <div className="bobine-fronton" data-testid="home-fronton" style={{ marginTop: 0, marginBottom: 24 }}>
            <p className="bobine-fronton__title">Bienvenue dans votre sanctuaire</p>
            <p className="bobine-fronton__subtitle">Le fil de votre histoire commence ici. Chaque photo que vous déposez est une empreinte que vous laissez au temps.</p>
          </div>

          {/* Slogan */}
          <div className="text-center mb-14">
            <img
              src="/bobine-face-logo.png"
              alt=""
              style={{
                width: 64, height: 64, objectFit: 'contain',
                margin: '0 auto 20px', display: 'block',
                animation: 'bobineGlow 3s ease-in-out infinite, bobineSpin 30s linear infinite',
              }}
            />
            <h2
              data-testid="home-slogan"
              style={{
                fontFamily: "'Georgia', 'Times New Roman', serif",
                fontSize: 32, fontWeight: 400, fontStyle: 'italic',
                color: '#D4AF37', letterSpacing: 1,
                textShadow: '0 0 20px rgba(212,175,55,0.3)',
              }}
            >
              {t('home_slogan')}
            </h2>
            <button
              data-testid="replay-onboarding-btn"
              onClick={() => window.dispatchEvent(new Event('bobine-replay-onboarding'))}
              style={{
                fontFamily: "'Georgia', serif", fontSize: 12,
                color: '#D4AF37', backgroundColor: 'transparent',
                border: '1px solid rgba(212,175,55,0.2)',
                padding: '8px 20px', borderRadius: 20, cursor: 'pointer',
                marginTop: 16, transition: 'all 0.3s',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#D4AF37'; e.currentTarget.style.borderColor = '#D4AF37'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#D4AF37'; e.currentTarget.style.borderColor = '#D4AF37'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              {t('home_discover')}
            </button>
            <button
              data-testid="scroll-to-fondatrice-btn"
              onClick={() => { const el = document.querySelector('[data-testid="bobine-vive-fondatrice"]'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              style={{
                fontFamily: "'Georgia', serif", fontSize: 12,
                color: '#D4AF37', backgroundColor: 'transparent',
                border: '1px solid rgba(212,175,55,0.2)',
                padding: '8px 20px', borderRadius: 20, cursor: 'pointer',
                marginTop: 10, transition: 'all 0.3s',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/></svg>
              Bobine Vive de la Fondatrice
            </button>
          </div>

          {/* Compteur retiré : chaque coffre est isolé. Aucun chiffre collectif n'apparaît. */}

          {/* Upload Zone — visible only when no result */}
          {!images.original && !uploading && (
            <div className="mb-10">
              <div
                data-testid="upload-zone"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${dragActive ? '#D4AF37' : 'rgba(212,175,55,0.25)'}`,
                  borderRadius: 12,
                  padding: '50px 20px',
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  backgroundColor: dragActive ? 'rgba(212,175,55,0.04)' : 'transparent',
                }}
              >
                {/* Icon */}
                <div style={{ marginBottom: 20 }}>
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto', opacity: 0.6 }}>
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
                  </svg>
                </div>
                <label
                  htmlFor="file-upload"
                  data-testid="upload-button"
                  style={{
                    display: 'inline-block',
                    fontFamily: "'Georgia', serif",
                    background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
                    padding: '14px 40px', borderRadius: 8,
                    fontSize: 16, fontWeight: 600, letterSpacing: 0.5,
                    cursor: 'pointer', transition: 'all 0.3s',
                    boxShadow: 'none',
                  }}
                  onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; }}
                >
                  {t('home_upload')}
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', marginTop: 16 }}>
                  {t('home_drag_drop')}
                </p>
              </div>
            </div>
          )}

          {/* Loading — Bobine avec fil doré */}
          {uploading && (
            <BobineLoader progress={uploadProgress} processed={processedCount} total={totalFiles} />
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-8">
              <p style={{ color: '#c0392b', fontFamily: "'Georgia', serif", fontSize: 14 }}>{error}</p>
              <button
                onClick={resetUpload}
                style={{
                  marginTop: 16, fontFamily: "'Georgia', serif",
                  backgroundColor: 'transparent', color: '#D4AF37',
                  border: '1px solid rgba(212,175,55,0.4)',
                  padding: '8px 24px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          {/* Comparison Slider Result */}
          {!uploading && !error && images.original && images.watermarked && (
            <div>
              {/* Modale de succès luxueuse */}
              {showSuccessReveal && (
                <div
                  data-testid="success-reveal"
                  onClick={() => setShowSuccessReveal(false)}
                  style={{
                    position: 'fixed', inset: 0, zIndex: 50,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'fadeIn 0.6s ease-out',
                  }}
                >
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      backgroundColor: '#0a0a0a',
                      border: '1px solid rgba(212,175,55,0.25)',
                      borderRadius: 16, padding: '48px 56px',
                      textAlign: 'center', maxWidth: 400,
                      boxShadow: '0 0 60px rgba(212,175,55,0.08)',
                      animation: 'fadeInUp 0.7s ease-out',
                    }}
                  >
                    <img
                      src="/bobine-face-logo.png"
                      alt=""
                      style={{
                        width: 64, height: 64, objectFit: 'contain',
                        margin: '0 auto 28px',
                        filter: 'drop-shadow(0 0 16px rgba(212,175,55,0.4))',
                        animation: 'bobine-spin 10s linear infinite',
                      }}
                    />
                    <p style={{
                      fontFamily: "'Georgia', serif",
                      fontSize: 17, color: '#D4AF37',
                      lineHeight: 1.7, letterSpacing: 0.5,
                      margin: 0,
                    }}>
                      {processedCount > 1
                        ? `Vos ${processedCount} souvenirs sont désormais\nscellés dans le Coffre-fort.`
                        : 'Votre souvenir est désormais\nscellé dans le Coffre-fort.'}
                    </p>
                    <button
                      data-testid="success-reveal-close"
                      onClick={() => setShowSuccessReveal(false)}
                      style={{
                        marginTop: 32, fontFamily: "'Georgia', serif",
                        backgroundColor: 'transparent', color: '#D4AF37',
                        border: '1px solid rgba(212,175,55,0.15)',
                        padding: '8px 28px', borderRadius: 20,
                        fontSize: 12, letterSpacing: 2, cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={e => { e.target.style.color = '#D4AF37'; e.target.style.borderColor = '#D4AF37'; }}
                      onMouseLeave={e => { e.target.style.color = '#D4AF37'; e.target.style.borderColor = '#D4AF37'; }}
                    >
                      DÉCOUVRIR
                    </button>
                  </div>
                </div>
              )}

              {/* Slider */}
              <ComparisonSlider
                beforeSrc={images.original}
                afterSrc={images.watermarked}
              />

              {/* Actions */}
              <div className="flex flex-wrap items-center justify-center gap-4" style={{ marginTop: 32 }}>
                {/* Sauvegarder — bouton principal Or */}
                <button
                  onClick={paymentSuccess ? handleSaveToTresors : handleDownloadOrSave}
                  data-testid="save-coffre-button"
                  style={{
                    fontFamily: "'Georgia', serif",
                    backgroundColor: saved ? '#2d7a3a' : '#D4AF37',
                    color: saved ? '#FAF3E0' : '#000',
                    border: 'none', padding: '14px 36px', borderRadius: 8,
                    fontSize: 15, fontWeight: 600, cursor: 'pointer',
                    boxShadow: 'none',
                    transition: 'all 0.3s', letterSpacing: 0.5,
                  }}
                  onMouseEnter={e => { if (!saved) { e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                  onMouseLeave={e => { if (!saved) { e.currentTarget.style.transform = 'translateY(0)'; } }}
                >
                  {saved ? t('home_saved') : t('home_save')}
                </button>

                {/* Télécharger */}
                {paymentSuccess ? (
                  <a
                    href={images.watermarked}
                    download="bobine-souvenir.jpg"
                    data-testid="download-button"
                    style={{
                      fontFamily: "'Georgia', serif",
                      backgroundColor: 'transparent', color: '#D4AF37',
                      border: '1px solid rgba(212,175,55,0.4)',
                      padding: '14px 36px', borderRadius: 8,
                      fontSize: 15, cursor: 'pointer', textDecoration: 'none',
                      transition: 'all 0.3s',
                    }}
                  >
                    Télécharger
                  </a>
                ) : (
                  <button
                    onClick={handleDownloadOrSave}
                    data-testid="download-button"
                    style={{
                      fontFamily: "'Georgia', serif",
                      backgroundColor: 'transparent', color: '#D4AF37',
                      border: '1px solid rgba(212,175,55,0.4)',
                      padding: '14px 36px', borderRadius: 8,
                      fontSize: 15, cursor: 'pointer',
                      transition: 'all 0.3s',
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = '#D4AF37'; e.target.style.backgroundColor = 'rgba(212,175,55,0.08)'; }}
                    onMouseLeave={e => { e.target.style.borderColor = '#D4AF37'; e.target.style.backgroundColor = 'transparent'; }}
                  >
                    Télécharger
                  </button>
                )}

                {/* Nouveau souvenir */}
                <button
                  onClick={resetUpload}
                  data-testid="new-upload-button"
                  style={{
                    fontFamily: "'Georgia', serif",
                    backgroundColor: 'transparent', color: '#D4AF37',
                    border: '1px solid rgba(212,175,55,0.15)',
                    padding: '14px 28px', borderRadius: 8,
                    fontSize: 14, cursor: 'pointer',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={e => { e.target.style.color = '#D4AF37'; e.target.style.borderColor = '#D4AF37'; }}
                  onMouseLeave={e => { e.target.style.color = '#D4AF37'; e.target.style.borderColor = '#D4AF37'; }}
                >
                  Nouveau souvenir
                </button>
              </div>

              {/* Storage full message */}
              {storageFull && (
                <div data-testid="storage-full-message" className="text-center" style={{
                  marginTop: 20, padding: '16px 24px',
                  border: '1px solid rgba(212,175,55,0.25)',
                  borderRadius: 10, backgroundColor: 'rgba(212,175,55,0.04)',
                }}>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', marginBottom: 12, lineHeight: 1.5 }}>
                    {storageFull}
                  </p>
                  <button
                    onClick={() => navigate('/offres')}
                    data-testid="upgrade-plan-button"
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600,
                      background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000',
                      border: 'none', padding: '10px 28px', borderRadius: 6,
                      cursor: 'pointer', transition: 'all 0.3s',
                      boxShadow: 'none',
                    }}
                  >
                    Découvrir l'Héritage
                  </button>
                </div>
              )}

              {/* Payment polling status */}
              {pollStatus && pollStatus !== 'success' && (
                <div className="text-center" style={{ marginTop: 16 }}>
                  <p style={{
                    fontFamily: "'Georgia', serif", fontSize: 13,
                    color: pollStatus === 'pending' ? '#D4AF37' :
                           pollStatus === 'timeout' ? '#c0392b' : 'rgba(212,175,55,0.4)',
                  }}>
                    {pollStatus === 'pending' && 'Vérification du paiement en cours...'}
                    {pollStatus === 'timeout' && 'Délai dépassé. Vérifiez votre email pour la confirmation.'}
                    {pollStatus === 'expired' && 'La session a expiré. Veuillez réessayer.'}
                    {pollStatus === 'error' && 'Erreur de vérification. Contactez le support.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Paywall Modal */}
      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />

      {/* ══════ Bobine Vive de la Fondatrice ══════ */}
      <section data-testid="bobine-vive-fondatrice" style={{
        padding: '60px 20px', maxWidth: 700, margin: '0 auto',
        textAlign: 'center',
      }}>
        <div style={{
          padding: '40px 32px', borderRadius: 16,
          background: 'linear-gradient(135deg, rgba(212,175,55,0.03), rgba(212,175,55,0.01))',
          border: '1px solid rgba(212,175,55,0.12)',
        }}>
          <img src="/bobine-face-logo.png" alt="" style={{ width: 44, height: 44, margin: '0 auto 16px', objectFit: 'contain', display: 'block', animation: 'bobineGlow 3s ease-in-out infinite, bobineSpin 30s linear infinite' }} />
          <h3 style={{
            fontFamily: "'Georgia', serif", fontSize: 18, color: '#D4AF37',
            fontWeight: 400, margin: '0 0 6px', letterSpacing: 1,
          }}>
            Bobine Vive de la Fondatrice
          </h3>
          <p style={{
            fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37',
            lineHeight: 1.7, fontStyle: 'italic', margin: '0 0 24px',
          }}>
            {t('fondatrice_desc') || "L'histoire de Bobine, racontée par celle qui l'a imaginée."}
          </p>
          {/* Video Player — Lecteur luxe sans gadgets */}
          <div style={{
            maxWidth: 560, margin: '0 auto',
            aspectRatio: window.innerWidth < 768 ? '4 / 5' : '16 / 9',
          }}>
            <BobineVideoPlayer
              testId="fondatrice-video"
              sourceSrc={`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api/fondatrice/video`}
              sourceType="video/mp4"
              subtitleBaseUrl={`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api/fondatrice/subtitles`}
              style={{
                border: '1px solid rgba(212,175,55,0.15)',
                boxShadow: '0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(212,175,55,0.05)',
              }}
            />
          </div>

          {/* Share buttons */}
          <div data-testid="fondatrice-share-buttons" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            marginTop: 18, flexWrap: 'wrap',
          }}>
            <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', letterSpacing: 1, fontWeight: 500 }}>
              {t('share_video') || 'Partager'}
            </span>
            {/* WhatsApp */}
            <button
              data-testid="share-whatsapp"
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Découvrez Bobine, le fil de la mémoire — ' + window.location.origin)}`, '_blank')}
              style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                backgroundColor: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </button>
            {/* Facebook */}
            <button
              data-testid="share-facebook"
              onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}`, '_blank')}
              style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                backgroundColor: 'rgba(66,103,178,0.08)', border: '1px solid rgba(66,103,178,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#4267B2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </button>
            {/* Copy link — with visual feedback */}
            <button
              data-testid="share-copy-link"
              onClick={(e) => {
                const btn = e.currentTarget;
                const url = window.location.origin;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(url).then(() => {
                    btn.style.borderColor = '#27ae60';
                    btn.querySelector('svg').style.stroke = '#27ae60';
                    const lbl = btn.querySelector('[data-copy-label]');
                    if (lbl) { lbl.textContent = 'Copié !'; lbl.style.color = '#27ae60'; }
                    setTimeout(() => { btn.style.borderColor = '#D4AF37'; btn.querySelector('svg').style.stroke = '#D4AF37'; if (lbl) { lbl.textContent = 'Copier le lien'; lbl.style.color = '#D4AF37'; } }, 2000);
                  }).catch(() => {
                    window.prompt('Copiez ce lien :', url);
                  });
                } else {
                  window.prompt('Copiez ce lien :', url);
                }
              }}
              style={{
                height: 34, borderRadius: 17, cursor: 'pointer', padding: '0 14px 0 10px',
                backgroundColor: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'all 0.3s',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              <span data-copy-label style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', letterSpacing: 0.5, fontWeight: 500, whiteSpace: 'nowrap' }}>Copier le lien</span>
            </button>
          </div>

          <p style={{
            fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37',
            marginTop: 14, fontStyle: 'italic', fontWeight: 400, letterSpacing: 0.3,
          }}>
            Une creation Coup de Projet
          </p>

          {/* ── Compteur de vues "Bobine Vive" (italique Or, charte stricte) ── */}
          <FondatriceViewCounter />

          {/* Trial offer CTA under the video */}
          <div data-testid="fondatrice-trial-cta" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '14px 24px', borderRadius: 24, marginTop: 22,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))',
            border: '1px solid rgba(212,175,55,0.2)',
            cursor: 'pointer', transition: 'all 0.3s',
          }}
            onClick={() => navigate('/offres')}
          >
            <img src="/bobine-reel-transparent.png" alt="" style={{ width: 18, height: 18, objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(212,175,55,0.4))' }} />
            <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontWeight: 500, letterSpacing: 0.3 }}>
              {t('fondatrice_trial_cta') || "D\u00e9couvrez l'\u00e9crin Bobine\u00a0: 30 jours de s\u00e9r\u00e9nit\u00e9 offerts"}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </div>
      </section>

      {/* ══════ Témoignages — Ils ont scellé leurs souvenirs ══════ */}
      <TestimonialsSection />

      {/* Footer */}
      <footer style={{ borderTop: '1px solid rgba(212,175,55,0.15)', marginTop: 80, padding: '24px 0' }}>
        <div className="text-center">
          <BobineLogo size={14} />
          <p style={{ fontFamily: "'Georgia', serif", color: '#D4AF37', fontSize: 11, marginTop: 4 }}>Une création Coup de Projet</p>
          <button
            onClick={() => window.dispatchEvent(new Event('bobine-replay-onboarding'))}
            data-testid="replay-onboarding-footer"
            style={{
              fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37',
              background: 'none', border: 'none', cursor: 'pointer', marginTop: 8,
              padding: '4px 8px', transition: 'color 0.3s',
            }}
            onMouseEnter={e => e.target.style.color = '#D4AF37'}
            onMouseLeave={e => e.target.style.color = '#D4AF37'}
          >
            {t('home_replay')}
          </button>
          <div style={{ marginTop: 12, display: 'flex', gap: 16, justifyContent: 'center' }}>
            <a href="/terms" data-testid="footer-terms-link" style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', textDecoration: 'none', transition: 'color 0.3s' }}
              onMouseEnter={e => e.target.style.color = '#D4AF37'}
              onMouseLeave={e => e.target.style.color = '#D4AF37'}
            >CGU</a>
            <a href="/privacy" data-testid="footer-privacy-link" style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', textDecoration: 'none', transition: 'color 0.3s' }}
              onMouseEnter={e => e.target.style.color = '#D4AF37'}
              onMouseLeave={e => e.target.style.color = '#D4AF37'}
            >Confidentialit\u00e9</a>
            <a href="mailto:bonjour@bobine.live" data-testid="footer-contact-link" style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', textDecoration: 'none', transition: 'color 0.3s' }}
              onMouseEnter={e => e.target.style.color = '#D4AF37'}
              onMouseLeave={e => e.target.style.color = '#D4AF37'}
            >Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bobine_user')); } catch { return null; }
  });

  // Utilisateur déjà connecté → pas d'onboarding
  const isReturningUser = !!localStorage.getItem('bobine_token');
  const publicPaths = ['/admin', '/admin/sas-alerts', '/services', '/offres', '/livre-photo', '/numerisation', '/compte', '/terms', '/privacy', '/coffret-temoin', '/faq', '/univers', '/demo'];
  // Routes privées de l'app : NE JAMAIS y montrer l'onboarding (ce serait un mix Accueil/Coffre).
  // Si non connecté, ces routes affichent AuthPage. Si connecté, elles affichent leur écran cible.
  const privateAppPaths = ['/dashboard'];
  const [showOnboarding, setShowOnboarding] = useState(() => {
    if (isReturningUser) return false;
    const p = window.location.pathname;
    if (publicPaths.includes(p) || privateAppPaths.includes(p) ||
        p.startsWith('/partage/') || p.startsWith('/admin/') || p.startsWith('/eclaireur/')) return false;
    return true;
  });

  // Désactiver onboarding dès qu'on visite une page publique OU privée de l'app
  useEffect(() => {
    if (publicPaths.includes(location.pathname) ||
        privateAppPaths.includes(location.pathname) ||
        location.pathname.startsWith('/partage/') ||
        location.pathname.startsWith('/admin/') ||
        location.pathname.startsWith('/eclaireur/')) {
      setShowOnboarding(false);
    }
  }, [location.pathname]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Verrouillage absolu : après l'onboarding, si pas connecté → page inscription/connexion.
    // Sinon → Coffre-fort (dashboard).
    const hasToken = !!localStorage.getItem('bobine_token');
    navigate(hasToken ? '/dashboard' : '/compte');
  };

  const handleLogin = (u) => setUser(u);

  const handleVerified = () => {
    // Mettre à jour l'utilisateur en localStorage
    const stored = JSON.parse(localStorage.getItem('bobine_user') || '{}');
    stored.email_verified = true;
    localStorage.setItem('bobine_user', JSON.stringify(stored));
    setUser(stored);
  };

  // Écouter la mise à jour utilisateur après paiement
  useEffect(() => {
    const onUpdate = () => {
      try {
        setUser(JSON.parse(localStorage.getItem('bobine_user')));
      } catch { /* silent */ }
    };
    const onReplay = () => setShowOnboarding(true);
    window.addEventListener('bobine-user-updated', onUpdate);
    window.addEventListener('bobine-replay-onboarding', onReplay);
    return () => {
      window.removeEventListener('bobine-user-updated', onUpdate);
      window.removeEventListener('bobine-replay-onboarding', onReplay);
    };
  }, []);

  if (showOnboarding) {
    return <OnboardingSlides onComplete={handleOnboardingComplete} />;
  }

  // Bloquer les utilisateurs non vérifiés
  const token = localStorage.getItem('bobine_token');
  if (user && token && user.email_verified === false) {
    return <VerifyEmail userEmail={user.email} token={token} onVerified={handleVerified} />;
  }

  return (
    <div className="App">
      <DemoExitBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/offres" element={<PricingPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/livre-photo" element={<LivrePhotoPage />} />
        <Route path="/numerisation" element={<NumerisationPage />} />
        <Route path="/compte" element={user ? <AccountPage /> : <AuthPage onLogin={handleLogin} />} />
        <Route path="/dashboard" element={user ? <Dashboard user={user} /> : <AuthPage onLogin={handleLogin} />} />
        <Route path="/admin" element={<ProtectedAdminRoute user={user}><AdminPage /></ProtectedAdminRoute>} />
        <Route path="/admin/sas-alerts" element={<ProtectedAdminRoute user={user}><SasAlertsPreviewPage /></ProtectedAdminRoute>} />
        <Route path="/partage/:shareToken" element={<SharedGalleryPage />} />
        <Route path="/eclaireur/:linkId" element={<EclaireurView />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/univers" element={<FaqPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/coffret-temoin" element={<CoffretTemoin />} />
        <Route path="/demo" element={<DemoPage />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
